import { hrubkaBucket, stranyNakresu } from "@/components/dielec/DielecVisual";
import { cn } from "@/lib/utils";

/**
 * Malý náčrt dielca v stĺpci typu (KOLO 180, návrh D) — obdĺžnik
 * v pomere strán a olepené strany čiarou vo farbe hrúbky hrany, ako
 * v detaile dielca (0,5 mm modrá, 1 mm fialová, 2 mm zelená). User
 * nemusí otvárať detail, aby videl, ktorá strana je olepená a čím.
 *
 * Farby sú o odtieň tmavšie než v detaile (600 namiesto 500), aby
 * tenká čiara prešla 3 : 1 na bielej. Hrana, ktorej hrúbku riadok
 * nepozná, je sivá.
 */

type Strana = "x1" | "x2" | "y1" | "y2";
const STRANY: readonly Strana[] = ["x1", "x2", "y1", "y2"];

const FARBA_HRANY: Record<string, string> = {
  "0.5": "stroke-sky-600",
  "1": "stroke-violet-600",
  "2": "stroke-emerald-600",
};

const SIRKA = 34;
const VYSKA = 22;
/** Najväčší obdĺžnik dosky — zvyšok je okraj pre čiaru hrany. */
const MAX_W = 26;
const MAX_H = 16;

interface Props {
  /** Rozmer v ose X a Y dielca (mm) — orientáciu rieši `smer`. */
  xMm: number | null;
  yMm: number | null;
  /** Smer vlákien — „Y" je otočený dielec, ako v stĺpcoch Dĺžka/Šírka. */
  smer?: string | null;
  /** Ktoré strany majú hranu (ID hrany na dielci). */
  hrany: Partial<Record<Strana, boolean>>;
  /** Hrúbka hrany per strana z backendu (`hrany_mm`). */
  hrubky?: Partial<Record<string, string | number>>;
  /** Masív — namiesto hrán zaoblené strany, čiarkovane (KOLO 168). */
  zaoblenie?: Partial<Record<Strana, boolean>>;
  /** Rodič sendviča — dve dosky nad sebou. */
  sendvic?: boolean;
}

function cislo(v: string | number | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Text do `title` — „X1 1 mm · Y2 0,5 mm", alebo „Bez hrán". */
export function popisHranNacrtu(props: Pick<Props, "hrany" | "hrubky" | "zaoblenie">): string {
  const casti: string[] = [];
  for (const s of STRANY) {
    if (props.zaoblenie?.[s]) {
      casti.push(`${s.toUpperCase()} zaoblená`);
    } else if (props.hrany[s]) {
      const h = cislo(props.hrubky?.[s]);
      casti.push(
        h == null ? s.toUpperCase() : `${s.toUpperCase()} ${String(h).replace(".", ",")} mm`,
      );
    }
  }
  return casti.length ? `Hrany: ${casti.join(" · ")}` : "Bez hrán";
}

export function NacrtDielca({
  xMm,
  yMm,
  smer,
  hrany,
  hrubky,
  zaoblenie,
  sendvic = false,
}: Props): JSX.Element {
  const otoceny = smer === "Y";
  const [d, s] = otoceny ? [yMm, xMm] : [xMm, yMm];
  let w = MAX_W;
  let h = MAX_H;
  if (d && s && d > 0 && s > 0) {
    h = (MAX_W * s) / d;
    if (h > MAX_H) {
      h = MAX_H;
      w = (MAX_H * d) / s;
    }
  }
  // Úzka lišta by bola čiarka — minimum, aby bolo vidno obe dlhé strany
  w = Math.max(w, 6);
  h = Math.max(h, 5);
  const x0 = (SIRKA - w) / 2;
  const y0 = (VYSKA - h) / 2;
  const { hore, dole, vlavo, vpravo } = stranyNakresu(otoceny);
  const ciary: Record<Strana, [number, number, number, number]> = {
    [hore]: [x0, y0, x0 + w, y0],
    [dole]: [x0, y0 + h, x0 + w, y0 + h],
    [vlavo]: [x0, y0, x0, y0 + h],
    [vpravo]: [x0 + w, y0, x0 + w, y0 + h],
  } as Record<Strana, [number, number, number, number]>;

  return (
    <svg
      aria-hidden
      data-nacrt
      width={SIRKA}
      height={VYSKA}
      viewBox={`0 0 ${SIRKA} ${VYSKA}`}
      className="mx-auto block"
    >
      {sendvic ? (
        <rect
          x={x0 + 2.5}
          y={y0 - 2.5}
          width={w}
          height={h}
          rx={0.5}
          className="fill-background stroke-tab-strom"
          strokeWidth={1}
        />
      ) : null}
      <rect
        x={x0}
        y={y0}
        width={w}
        height={h}
        rx={0.5}
        className="fill-background stroke-tab-strom"
        strokeWidth={1}
      />
      {STRANY.map((strana) => {
        const [x1, y1, x2, y2] = ciary[strana];
        if (zaoblenie?.[strana]) {
          return (
            <line
              key={strana}
              data-strana={strana}
              data-zaoblenie
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              strokeWidth={2}
              strokeDasharray="2 2"
              className="stroke-muted-foreground"
            />
          );
        }
        if (!hrany[strana]) return null;
        const bucket = hrubkaBucket(cislo(hrubky?.[strana]));
        return (
          <line
            key={strana}
            data-strana={strana}
            data-hrubka={bucket ?? ""}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            strokeWidth={bucket === "2" ? 3 : 2.5}
            strokeLinecap="square"
            className={cn(bucket ? FARBA_HRANY[bucket] : "stroke-muted-foreground")}
          />
        );
      })}
    </svg>
  );
}
